import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Clock, CalendarDays, RefreshCw, LogOut, PenLine, ChevronDown, BarChart3, Settings } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ColorLegend } from "@/components/ColorLegend";
import { LevelDisplay, levelMeaning, EvalLegend } from "@/lib/evalSystem";

const LEGEND_INTRO =
  "Les niveaux ci-dessous représentent ce que l'enseignant a observé cette semaine pour votre enfant.";

function formatRange(startISO, endISO) {
  try {
    const f = (d, o) => new Intl.DateTimeFormat("fr-FR", o).format(new Date(d));
    return `Semaine du ${f(startISO, { day: "numeric", month: "long" })} au ${f(endISO, {
      day: "numeric", month: "long", year: "numeric",
    })}`;
  } catch { return "Semaine en cours"; }
}

function formatSignedAt(iso) {
  try {
    const d = new Date(iso);
    const day = new Intl.DateTimeFormat("fr-FR", {
      day: "numeric", month: "long", year: "numeric", timeZone: "Europe/Paris",
    }).format(d);
    const parts = new Intl.DateTimeFormat("fr-FR", {
      hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Europe/Paris",
    }).formatToParts(d);
    const h = parts.find((p) => p.type === "hour")?.value ?? "00";
    const m = parts.find((p) => p.type === "minute")?.value ?? "00";
    return `le ${day} à ${h} h ${m}`;
  } catch { return ""; }
}

function formatUpdated(iso) {
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "numeric", month: "long", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris",
    }).format(new Date(iso)).replace(":", " h ");
  } catch { return "—"; }
}

// Affichage lecture seule des critères (système Le Passeport OU personnalisé).
function CriteriaList({ data, compact = false }) {
  const system = data.eval_system;
  const values = data.values || {};
  const criteria = system?.criteria || [];
  return (
    <div className={compact ? "flex flex-wrap gap-2" : "flex flex-col gap-3"}>
      {criteria.map((c) => {
        const levelId = values[c.id];
        const meaning = levelMeaning(system, levelId);
        if (compact) {
          return (
            <span key={c.id} className="flex items-center gap-1.5 rounded-full bg-slate-50 px-2.5 py-1 text-xs text-slate-600 dark:bg-white/5 dark:text-slate-300" title={`${c.label} : ${meaning}`} data-testid={`criteria-compact-${c.id}`}>
              <LevelDisplay system={system} levelId={levelId} /> {c.label}
            </span>
          );
        }
        return (
          <div key={c.id} className="flex items-start gap-3 rounded-xl border border-slate-100 p-3 dark:border-white/10" data-testid={`criteria-${c.id}`}>
            <span className="mt-0.5 shrink-0"><LevelDisplay system={system} levelId={levelId} /></span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{c.label}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{meaning}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ReadOnlyText({ label, value }) {
  return (
    <div>
      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p className="whitespace-pre-wrap rounded-xl bg-slate-50 px-3 py-2.5 text-sm text-slate-700 dark:bg-white/5 dark:text-slate-200">
        {value?.trim() ? value : <span className="text-slate-300">—</span>}
      </p>
    </div>
  );
}

// Bloc signature : bouton unique, puis message une fois signé.
function SignatureBlock({ passport, onSigned }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const sigs = passport.signatures || [];
  const signedList = sigs.length > 0 && (
    <ul className="flex flex-col gap-1.5" data-testid="signatures-list">
      {sigs.map((s) => (
        <li key={s.signed_at} className="flex items-center justify-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-400">
          <Check size={16} /> {s.parent_name} — {formatSignedAt(s.signed_at)}
        </li>
      ))}
    </ul>
  );

  if (passport.signed_by_me) {
    return (
      <div
        className="flex flex-col gap-2 rounded-2xl bg-emerald-50 px-4 py-4 text-center text-sm font-medium text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400"
        data-testid="signed-message"
      >
        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600/80">Passeport signé</p>
        {signedList}
      </div>
    );
  }

  const confirm = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/parent/passports/${passport.passport_id}/sign`);
      setOpen(false);
      toast.success("Passeport signé. Merci !");
      onSigned(data);
    } catch {
      toast.error("La signature a échoué. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {sigs.length > 0 && (
        <div className="rounded-2xl bg-emerald-50 px-4 py-3 dark:bg-emerald-500/10" data-testid="other-signatures">
          <p className="mb-1 text-center text-xs font-semibold uppercase tracking-wide text-emerald-600/80">Déjà signé par</p>
          {signedList}
        </div>
      )}
      <DialogTrigger asChild>
        <button
          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 py-4 text-base font-semibold text-white transition-transform duration-200 hover:scale-[0.99] active:scale-95 dark:bg-white dark:text-slate-900"
          data-testid="sign-button"
        >
          <PenLine size={18} /> Signer le Passeport
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-2xl sm:max-w-[380px]">
        <DialogHeader>
          <DialogTitle>Signer le Passeport ?</DialogTitle>
          <DialogDescription>
            En signant, vous confirmez avoir pris connaissance du Passeport de la semaine.
            La date et l'heure seront enregistrées.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <button
            onClick={confirm}
            disabled={loading}
            className="w-full rounded-xl bg-slate-900 py-3 font-semibold text-white transition-transform duration-200 hover:scale-[0.99] active:scale-95 disabled:opacity-60 dark:bg-white dark:text-slate-900"
            data-testid="confirm-sign-button"
          >
            {loading ? "Signature…" : "Confirmer la signature"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ParentHome() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [children, setChildren] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/parent/children");
        setChildren(data.children);
        if (data.children.length) setActiveId(data.children[0].student_id);
        else setLoading(false);
      } catch {
        toast.error("Chargement impossible.");
        setLoading(false);
      }
    })();
  }, []);

  const loadChild = useCallback(async (sid) => {
    setLoading(true);
    try {
      const [w, h] = await Promise.all([
        api.get(`/parent/children/${sid}/week`, { params: { offset: 0 } }),
        api.get(`/parent/children/${sid}/history`),
      ]);
      setCurrent(w.data);
      setHistory(h.data.history);
    } catch {
      toast.error("Chargement impossible.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeId) loadChild(activeId);
  }, [activeId, loadChild]);

  const handleSigned = (sig) => {
    const apply = (p) => ({
      ...p, signed: true, signed_by_me: true, signed_at: p.signed_at || sig.signed_at, signed_by: sig.signed_by,
      signatures: [...(p.signatures || []), { parent_name: sig.signed_by, signed_at: sig.signed_at }],
    });
    setCurrent((c) => apply(c));
    setHistory((hist) => hist.map((p) => (p.week_number === current?.week_number ? apply(p) : p)));
  };

  const previousWeeks = history.filter((p) => p.week_number !== current?.week_number);

  return (
    <div className="min-h-screen w-full bg-[#FAFAFA] dark:bg-[#0A0A0A]">
      <header className="sticky top-0 z-10 border-b border-slate-100 bg-white/85 backdrop-blur-xl dark:border-white/10 dark:bg-[#0A0A0A]/85">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2.5">
            <Logo size={34} />
            <span className="text-sm font-semibold tracking-tight text-slate-900 dark:text-slate-100">Le Passeport</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/parent/resume")}
              className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 transition-transform duration-200 hover:scale-95 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
              data-testid="summary-nav-button"
            >
              <BarChart3 size={16} /> <span className="hidden sm:inline">Résumé</span>
            </button>
            <button
              onClick={() => navigate("/parent/parametres")}
              className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 transition-transform duration-200 hover:scale-95 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
              data-testid="settings-nav-button"
            >
              <Settings size={16} /> <span className="hidden sm:inline">Paramètres</span>
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 transition-transform duration-200 hover:scale-95 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
              data-testid="logout-button"
            >
              <LogOut size={16} /> <span className="hidden sm:inline">Quitter</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-6">
        {(() => {
          if (loading && !current) return (
          <div className="flex justify-center py-24" data-testid="parent-loading">
            <RefreshCw className="animate-spin text-slate-400" size={26} />
          </div>
          );
          if (children && children.length === 0) return (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-white/50 py-16 text-center text-slate-500" data-testid="no-child">
            Aucun enfant n'est associé à votre compte pour le moment.
          </div>
          );
          if (!current) return null;
          return (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: "easeOut" }}
            className="flex flex-col gap-6"
          >
            {/* Sélecteur d'enfant (si plusieurs) */}
            {children && children.length > 1 && (
              <div className="flex flex-wrap gap-2" data-testid="child-selector">
                {children.map((c) => (
                  <button
                    key={c.student_id}
                    onClick={() => setActiveId(c.student_id)}
                    className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                      c.student_id === activeId
                        ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                        : "bg-white text-slate-600 border border-slate-200 dark:bg-white/5 dark:text-slate-300"
                    }`}
                    data-testid={`child-tab-${c.student_id}`}
                  >
                    {c.first_name}
                  </button>
                ))}
              </div>
            )}

            {/* En-tête enfant */}
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100" data-testid="child-name">
                {current.student_name}
              </h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {current.class_name}{current.class_level ? ` · ${current.class_level}` : ""}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                <span className="flex items-center gap-1.5" data-testid="week-label">
                  <CalendarDays size={13} /> {formatRange(current.week_start, current.week_end)}
                </span>
                <span>Mis à jour le {formatUpdated(current.updated_at)}</span>
              </div>
            </div>

            {current.published === false ? (
              <section className="rounded-3xl border border-dashed border-slate-200 bg-white/60 p-8 text-center dark:border-white/10 dark:bg-white/[0.02]" data-testid="not-published">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400 dark:bg-white/10">
                  <Clock size={22} />
                </div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Le Passeport de cette semaine n'est pas encore disponible.
                </p>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Vous serez informé par e-mail dès que l'enseignant l'aura publié.
                </p>
              </section>
            ) : (
              <>
                {/* Passeport de la semaine */}
                <section className="rounded-3xl border border-slate-100 bg-white p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] dark:border-white/10 dark:bg-white/[0.03]" data-testid="current-passport">
                  <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Le Passeport de la semaine</h2>
                  <CriteriaList data={current} />
                  {current.eval_system?.type === "passeport" ? (
                    <ColorLegend showIntro className="mt-5 border-t border-slate-100 pt-4 dark:border-white/10" />
                  ) : (
                    <div className="mt-5 border-t border-slate-100 pt-4 dark:border-white/10">
                      <p className="mb-2.5 text-xs text-slate-500 dark:text-slate-400">{LEGEND_INTRO}</p>
                      <EvalLegend system={current.eval_system} />
                    </div>
                  )}
                  <div className="mt-5 flex flex-col gap-4">
                    <ReadOnlyText label="Notions à revoir" value={current.notions_a_revoir} />
                    <ReadOnlyText label="Commentaire" value={current.commentaire} />
                  </div>
                </section>

                {/* Signature */}
                <SignatureBlock passport={current} onSigned={handleSigned} />
              </>
            )}

            {/* Historique */}
            {previousWeeks.length > 0 && (
              <section data-testid="history-section">
                <h2 className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200">
                  <ChevronDown size={16} /> Semaines précédentes
                </h2>
                <div className="flex flex-col gap-3">
                  {previousWeeks.map((p) => (
                    <div
                      key={p.passport_id}
                      className="rounded-2xl border border-slate-100 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03]"
                      data-testid={`history-item-${p.week_number}`}
                    >
                      <div className="mb-3 flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                          {formatRange(p.week_start, p.week_end)}
                        </span>
                        {p.signed ? (
                          <span className="flex items-center gap-1 text-xs font-medium text-emerald-600">
                            <Check size={13} /> Signé
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs font-medium text-amber-600">
                            <Clock size={13} /> Non signé
                          </span>
                        )}
                      </div>
                      <CriteriaList data={p} compact />
                      {(p.notions_a_revoir?.trim() || p.commentaire?.trim()) && (
                        <div className="mt-3 flex flex-col gap-2 text-sm text-slate-600 dark:text-slate-300">
                          {p.notions_a_revoir?.trim() && (
                            <p><span className="text-slate-400">Notions : </span>{p.notions_a_revoir}</p>
                          )}
                          {p.commentaire?.trim() && (
                            <p><span className="text-slate-400">Commentaire : </span>{p.commentaire}</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}
          </motion.div>
          );
        })()}
      </main>
    </div>
  );
}
